import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import dayjs from 'dayjs';

const STATUS_LABELS = {
  confirmed: 'Bestätigt',
  canceled: 'Storniert',
  awaitingConfirmation: 'Wartet auf Bestätigung',
  rejected: 'Abgelehnt',
  waitingForPayment: 'Wartet auf Zahlung',
  blocked: 'Blockiert',
};

const CHANGED_BY_LABELS = {
  'public-confirm': 'Selbstbedienung (Kunde)',
  'public-comment': 'Selbstbedienung (Kunde)',
  system: 'System',
};

const KEY_LABELS = {
  participantCount: 'Teilnehmerzahl',
  totalAmount: 'Gesamtbetrag',
  status: 'Status',
  startTime: 'Startzeit',
  endTime: 'Endzeit',
  selectedPackage: 'Paket',
  'lineItems.package': 'Paket hinzugefügt',
  'lineItems.package.count': 'Paket-Anzahl',
  'lineItems.package.status': 'Paket-Status',
  'upsell.added': 'Zusatz hinzugefügt',
  'upsell.removed': 'Zusatz entfernt',
  'upsell.count': 'Zusatz-Anzahl',
  resourceId: 'Ressource',
  voucherRemoved: 'Voucher entfernt',
  capacityParticipantCount: 'Kapazitätsübersteuerung Teilnehmer',
  capacityLaneCount: 'Kapazitätsübersteuerung Bahnen',
  customerComment: 'Kundenhinweis',
};

function formatValue(key, value) {
  if (value == null || value === 'null' || value === '') return '—';

  if (key === 'status') {
    return STATUS_LABELS[value] || value;
  }

  if (key === 'startTime' || key === 'endTime') {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : String(value);
  }

  if (key === 'totalAmount') {
    const num = Number(value);
    return Number.isFinite(num) ? `${num.toFixed(2).replace('.', ',')} €` : String(value);
  }

  if (key === 'upsell.added' || key === 'upsell.count' || key === 'upsell.removed') {
    try {
      const obj = typeof value === 'string' ? JSON.parse(value) : value;
      if (obj?.name) {
        return obj.count != null ? `${obj.name} (Anzahl: ${obj.count})` : obj.name;
      }
    } catch {
      // fallback to raw
    }
  }

  if (key === 'selectedPackage' || key === 'lineItems.package') {
    try {
      const obj = typeof value === 'string' ? JSON.parse(value) : value;
      const name = obj?.name ?? obj?.description;
      if (name) {
        return obj.count != null ? `${name} (×${obj.count})` : name;
      }
    } catch {
      // fallback to raw
    }
  }

  if (key === 'lineItems.package.status') {
    return value === 'active' ? 'Aktiv' : value === 'canceled' ? 'Storniert' : value;
  }

  if (key === 'resourceId') {
    return value;
  }

  return String(value);
}

function deriveEntrySummary(keys) {
  const keySet = new Set(keys.map(k => k.key));

  if (keySet.has('status')) {
    const statusKey = keys.find(k => k.key === 'status');
    const oldVal = statusKey?.oldValue;
    const newVal = statusKey?.newValue;
    if (oldVal === 'awaitingConfirmation' && newVal === 'confirmed') return 'Buchungsanfrage angenommen';
    if (newVal === 'rejected') return 'Buchungsanfrage abgelehnt';
    if (newVal === 'canceled') return 'Buchung storniert';
  }

  if (keySet.has('participantCount') && !keySet.has('status')) {
    const pc = keys.find(k => k.key === 'participantCount');
    const oldVal = Number(pc?.oldValue);
    const newVal = Number(pc?.newValue);
    if (Number.isFinite(oldVal) && Number.isFinite(newVal)) {
      if (newVal > oldVal) return 'Teilnehmer hinzugefügt';
      if (newVal < oldVal) return 'Teilnehmer storniert';
    }
  }

  if (keySet.has('customerComment')) {
    const c = keys.find(k => k.key === 'customerComment');
    if (!c?.newValue) return 'Kundenhinweis entfernt';
    return c?.oldValue ? 'Kundenhinweis geändert' : 'Kundenhinweis hinterlassen';
  }

  if (keySet.has('startTime') || keySet.has('endTime')) return 'Buchung verschoben';
  if (keySet.has('selectedPackage')) return 'Paket geändert';
  if (keySet.has('upsell.added')) return 'Zusatz hinzugefügt';
  if (keySet.has('upsell.removed')) return 'Zusatz entfernt';
  if (keySet.has('upsell.count')) return 'Zusatz-Anzahl geändert';
  if (keySet.has('resourceId')) return 'Ressource geändert';
  if (keySet.has('voucherRemoved')) return 'Voucher entfernt';
  if (keySet.has('capacityParticipantCount') || keySet.has('capacityLaneCount'))
    return 'Kapazitätsübersteuerung geändert';

  return 'Buchung geändert';
}

function BookingHistoryModal({ open, onClose, booking }) {
  const entries = useMemo(() => {
    const history = Array.isArray(booking?.history) ? booking.history : [];
    return [...history].reverse();
  }, [booking?.history]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Änderungsverlauf</DialogTitle>
      <DialogContent>
        {entries.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            Keine Änderungen erfasst
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {entries.map((entry, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant='body2' fontWeight={600}>
                    {deriveEntrySummary(entry.keys)}
                  </Typography>
                  <Chip
                    label={dayjs(entry.changedAt).format('DD.MM.YYYY HH:mm')}
                    size='small'
                    variant='outlined'
                  />
                </Box>
                <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
                  Durchgeführt von{' '}
                  {CHANGED_BY_LABELS[entry.changedBy] ?? entry.changedBy}
                </Typography>
                <Box component='ul' sx={{ m: 0, pl: 2, '& li': { mb: 0.5 } }}>
                  {entry.keys
                    .filter(k => {
                      if (
                        k.key === 'lineItems.package' &&
                        entry.keys.some(x => x.key === 'selectedPackage')
                      )
                        return false;
                      return true;
                    })
                    .map((k, i) => {
                      const label = KEY_LABELS[k.key] || k.key;
                      const oldFormatted = formatValue(k.key, k.oldValue);
                      const newFormatted = formatValue(k.key, k.newValue);

                      if (k.key === 'totalAmount' && entry.keys.length > 1) {
                        return (
                          <li key={i}>
                            <Typography variant='caption'>
                              {label}: {oldFormatted} → {newFormatted}
                            </Typography>
                          </li>
                        );
                      }

                      return (
                        <li key={i}>
                          <Typography variant='caption'>
                            {label}: {oldFormatted} → {newFormatted}
                          </Typography>
                        </li>
                      );
                    })}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}

export default BookingHistoryModal;
